import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, SelectItem, Spinner, Tab, Tabs, Tooltip } from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { applySort } from '../lib/sort';
import { useAuth } from '../hooks/useAuth';
import GameCommandCatalog from '../components/GameCommandCatalog';
import FeaturedGames from '../components/FeaturedGames';
import GamePolicyEditor from '../components/GamePolicyEditor';

// Galería de juegos: el catálogo sale del manifest real de producción
// (GET /api/games/manifest, el mismo que descarga la app) con sus portadas en
// R2, y el uso de la tabla D1 `gaming_stats` (plays y diamantes por juego).

const STORAGE_BASE = 'https://storage.tikcontrol.live/games';
const GROUP_OTHER = 'otros';
const GROUP_LABELS = { [GROUP_OTHER]: 'Otros juegos' };

const PROTOCOL_LABELS = {
  http: 'HTTP',
  tcp: 'TCP',
  file: 'Archivo',
  custom: 'Custom',
  internal: 'ServerTap'
};

const SORT_OPTIONS = [
  ['catalog', 'Orden del manifest'],
  ['plays', 'Mas jugados'],
  ['diamonds', 'Mas diamantes'],
  ['name', 'Nombre A-Z'],
  ['recent', 'Evento mas reciente']
];

function formatNum(n) {
  return Number(n || 0).toLocaleString('es-ES');
}

function formatTs(n) {
  if (!n) return '—';
  const d = new Date(n);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES');
}

// Los game_id de D1 no siempre coinciden con el manifest (geometrydash vs
// geometry-dash, supermarketsimulator vs supermarket-simulator…): se casan
// ignorando guiones y mayúsculas, sumando todas las variantes.
function normalizeId(id) {
  return String(id || '').toLowerCase().replace(/[-_\s]/g, '');
}

function initialsFor(name) {
  return String(name || '?')
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

function InfoTip({ text }) {
  return (
    <Tooltip
      placement="bottom"
      delay={150}
      closeDelay={0}
      content={<div className="max-w-xs px-1 py-1 text-xs leading-5 text-default-600">{text}</div>}
      classNames={{ content: 'border border-default-200 bg-content1 shadow-xl' }}
    >
      <span className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-default-300 text-[10px] font-semibold leading-none text-default-500 transition hover:border-default-500 hover:text-default-700">
        i
      </span>
    </Tooltip>
  );
}

function KpiStat({ title, value, info, accent, detail }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 px-5 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-default-500">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: accent }} />
        <span className="truncate">{title}</span>
        {info && <InfoTip text={info} />}
      </div>
      <div className="text-3xl font-semibold leading-none">{formatNum(value)}</div>
      {detail && <div className="text-xs text-default-500">{detail}</div>}
    </div>
  );
}

// Portada del manifest (R2). Si falla, prueba la ruta del Worker y, si no,
// iniciales sobre un fondo de color.
function GameCover({ game }) {
  const candidates = useMemo(() => {
    const list = [];
    if (game.cover) list.push(`${STORAGE_BASE}/${game.cover}`);
    list.push(`${(import.meta.env.VITE_API_BASE || 'https://ws.tikcontrol.live').replace(/\/+$/, '')}/api/games/${encodeURIComponent(game.id)}/cover`);
    return list;
  }, [game.id, game.cover]);
  const [index, setIndex] = useState(0);
  const src = candidates[index];
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setIndex((i) => i + 1)}
        className="h-full w-full object-cover"
      />
    );
  }
  const tone = game.group === 'minecraft'
    ? 'from-success-500/40 to-success-900/60'
    : game.group === 'gtav'
      ? 'from-warning-500/40 to-warning-900/60'
      : 'from-primary-500/40 to-primary-900/60';
  return (
    <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${tone}`}>
      <span className="text-3xl font-semibold tracking-wide text-white/80">{initialsFor(game.name)}</span>
    </div>
  );
}

function GameCard({ game, canWrite, resetting, onReset }) {
  const statusColor = game.status === 'beta' ? 'bg-secondary' : game.status === 'stable' || game.status === 'active' ? 'bg-success' : 'bg-default-400';
  return (
    <div className="group flex flex-col overflow-hidden rounded-lg border border-default-200 bg-content1 transition hover:border-default-400">
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-content2">
        <GameCover game={game} />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {game.devOnly && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-white">dev</span>}
          {game.status === 'beta' && <span className="rounded bg-warning px-1.5 py-0.5 text-[10px] font-semibold text-black">beta</span>}
        </div>
        {game.port ? (
          <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {PROTOCOL_LABELS[game.protocol] || game.protocol} · {game.port}
          </span>
        ) : (
          <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {PROTOCOL_LABELS[game.protocol] || game.protocol}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusColor}`} />
            <span className="truncate text-sm font-semibold" title={game.name}>{game.name}</span>
          </div>
          <code className="block truncate text-[11px] text-default-400">{game.id}</code>
        </div>
        {game.description && (
          <p className="line-clamp-2 text-[11px] leading-4 text-default-500" title={game.description}>{game.description}</p>
        )}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="text-xs text-default-500">
            <span className={`font-semibold ${game.plays > 0 ? 'text-foreground' : 'text-default-400'}`}>{formatNum(game.plays)}</span> plays
            <span className="mx-1 text-default-300">·</span>
            <span className={`font-semibold ${game.diamonds > 0 ? 'text-foreground' : 'text-default-400'}`}>{formatNum(game.diamonds)}</span> 💎
            {game.lastUpdated ? <div className="text-[11px] text-default-400">ultimo {formatTs(game.lastUpdated)}</div> : null}
          </div>
          {canWrite && game.statIds.length > 0 && (
            <Button
              size="sm"
              variant="flat"
              color="warning"
              className="h-7 min-w-0 px-2 text-xs"
              isLoading={resetting}
              onPress={() => onReset(game)}
            >
              Reset stats
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Gaming() {
  const { canWrite } = useAuth();
  const [manifest, setManifest] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [protocolFilter, setProtocolFilter] = useState('all');
  const [sort, setSort] = useState('catalog');
  const [toast, setToast] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [stats, man] = await Promise.all([
        apiFetch('/api/backoffice/gaming/stats'),
        apiFetch(`/api/games/manifest?backoffice=${Date.now()}`, { cache: 'no-store' }).catch((e) => ({ error: e.message }))
      ]);
      setRows(stats.rows || []);
      if (man?.error) setError(`No se pudo cargar el manifest de juegos: ${man.error}`);
      else setManifest(man);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // Refresh an open tab after catalog updates, including old cached responses.
    const refreshCatalog = () => {
      if (document.visibilityState !== 'visible') return;
      apiFetch(`/api/games/manifest?backoffice=${Date.now()}`, { cache: 'no-store' })
        .then(setManifest).catch(() => {});
    };
    window.addEventListener('focus', refreshCatalog);
    const timer = window.setInterval(refreshCatalog, 60000);
    return () => {
      window.removeEventListener('focus', refreshCatalog);
      window.clearInterval(timer);
    };
  }, []);

  const groups = useMemo(() => {
    const defined = Object.entries(manifest?.groups || {})
      .map(([key, value]) => ({ key, name: value?.name || key, order: Number(value?.order || 99) }))
      .sort((a, b) => a.order - b.order);
    return [...defined, { key: GROUP_OTHER, name: GROUP_LABELS[GROUP_OTHER], order: 999 }];
  }, [manifest]);
  const groupName = (key) => groups.find((g) => g.key === key)?.name || key;

  // Manifest + uso real (D1), casando ids normalizados.
  const games = useMemo(() => {
    const list = manifest?.games || [];
    const byNorm = new Map();
    for (const row of rows) {
      const norm = normalizeId(row.game_id);
      if (!byNorm.has(norm)) byNorm.set(norm, []);
      byNorm.get(norm).push(row);
    }
    return list.map((game, index) => {
      const matches = byNorm.get(normalizeId(game.id)) || [];
      return {
        ...game,
        order: index,
        group: game.group || GROUP_OTHER,
        plays: matches.reduce((acc, r) => acc + Number(r.total_plays || 0), 0),
        diamonds: matches.reduce((acc, r) => acc + Number(r.total_diamonds || 0), 0),
        lastUpdated: matches.reduce((acc, r) => Math.max(acc, Number(r.last_updated || 0)), 0),
        statIds: matches.map((r) => r.game_id)
      };
    });
  }, [manifest, rows]);

  // Filas de D1 que no casan con ningún juego del manifest.
  const uncatalogued = useMemo(() => {
    const known = new Set(games.map((g) => normalizeId(g.id)));
    return rows
      .filter((r) => !known.has(normalizeId(r.game_id)))
      .map((r) => ({ id: r.game_id, plays: Number(r.total_plays || 0), diamonds: Number(r.total_diamonds || 0), lastUpdated: Number(r.last_updated || 0) }))
      .sort((a, b) => b.plays - a.plays);
  }, [games, rows]);

  const totals = useMemo(() => rows.reduce((acc, r) => {
    acc.plays += Number(r.total_plays || 0);
    acc.diamonds += Number(r.total_diamonds || 0);
    if (Number(r.total_plays || 0) > 0) acc.active++;
    return acc;
  }, { plays: 0, diamonds: 0, active: 0 }), [rows]);

  const protocols = useMemo(() => [...new Set(games.map((g) => g.protocol).filter(Boolean))], [games]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = games.filter((g) => {
      if (groupFilter !== 'all' && g.group !== groupFilter) return false;
      if (protocolFilter !== 'all' && g.protocol !== protocolFilter) return false;
      if (!needle) return true;
      return (
        g.id.toLowerCase().includes(needle) ||
        (g.name || '').toLowerCase().includes(needle) ||
        (g.tags || []).some((tag) => String(tag).toLowerCase().includes(needle)) ||
        g.statIds.some((id) => id.toLowerCase().includes(needle))
      );
    });
    const sorters = {
      catalog: { column: 'order', direction: 'ascending' },
      plays: { column: 'plays', direction: 'descending' },
      diamonds: { column: 'diamonds', direction: 'descending' },
      name: { column: 'name', direction: 'ascending' },
      recent: { column: 'lastUpdated', direction: 'descending' }
    };
    return applySort(base, sorters[sort] || sorters.catalog, {
      order: (g) => g.order,
      plays: (g) => g.plays,
      diamonds: (g) => g.diamonds,
      name: (g) => (g.name || '').toLowerCase(),
      lastUpdated: (g) => g.lastUpdated
    });
  }, [games, search, groupFilter, protocolFilter, sort]);

  // Agrupado por grupo del manifest cuando se ve el catálogo completo en su orden.
  const sections = useMemo(() => {
    if (sort !== 'catalog' || groupFilter !== 'all') return [{ key: 'all', name: null, games: filtered }];
    return groups
      .map((g) => ({ key: g.key, name: g.name, games: filtered.filter((game) => game.group === g.key) }))
      .filter((section) => section.games.length > 0);
  }, [filtered, groups, sort, groupFilter]);

  const groupCounts = useMemo(() => {
    const acc = { all: games.length };
    for (const g of games) acc[g.group] = (acc[g.group] || 0) + 1;
    return acc;
  }, [games]);

  async function handleReset(game) {
    const ids = game.statIds;
    if (!confirm(`¿Resetear estadisticas de "${game.name}" (${ids.join(', ')})? Plays y diamantes vuelven a 0.`)) return;
    setResettingId(game.id);
    try {
      for (const id of ids) {
        await apiFetch(`/api/backoffice/gaming/stats/${encodeURIComponent(id)}/reset`, { method: 'POST' });
      }
      setToast({ type: 'ok', msg: `Stats reseteados para ${game.name}.` });
      reload();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setResettingId(null);
    }
  }

  async function handleResetRaw(id) {
    if (!confirm(`¿Resetear estadisticas de "${id}"? Plays y diamantes vuelven a 0.`)) return;
    setResettingId(id);
    try {
      await apiFetch(`/api/backoffice/gaming/stats/${encodeURIComponent(id)}/reset`, { method: 'POST' });
      setToast({ type: 'ok', msg: `Stats reseteados para ${id}.` });
      reload();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setResettingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Gaming</h1>
          <p className="text-sm text-default-500">
            {games.length ? `${games.length} juegos del manifest de produccion` : 'Juegos del manifest de produccion'}, con plays y diamantes reales.
          </p>
        </div>
        <Button size="sm" variant="flat" onPress={reload} isLoading={loading}>
          Refrescar
        </Button>
      </div>

      {canWrite && <GameCommandCatalog games={manifest?.games || []} />}
      {canWrite && <FeaturedGames games={manifest?.games||[]} />}
      {canWrite && <GamePolicyEditor games={manifest?.games||[]} onSaved={reload} />}
      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>
      )}
      {toast && (
        <div className={`rounded-md border p-3 text-sm ${toast.type === 'ok' ? 'border-success-200 bg-success-50 text-success-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>cerrar</button>
          </div>
        </div>
      )}

      {loading && !manifest ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-y-4 border-t border-default-200 pt-4 md:grid-cols-4 md:divide-x md:divide-default-200">
            <KpiStat title="Juegos" value={games.length} accent="#3b82f6" detail={`${groups.length - 1} grupos`} />
            <KpiStat title="Plays totales" value={totals.plays} accent="#22c55e" info="Suma de total_plays de gaming_stats (D1), todas las claves incluidas." />
            <KpiStat title="Diamantes" value={totals.diamonds} accent="#a855f7" info="Suma de total_diamonds de gaming_stats (D1)." />
            <KpiStat title="Con actividad" value={totals.active} accent="#f97316" detail={`de ${rows.length} claves en D1`} info="Claves de gaming_stats con al menos un play." />
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-default-200 pt-4">
            <Input
              placeholder="Buscar por nombre, id o tag…"
              value={search}
              onValueChange={setSearch}
              isClearable
              size="sm"
              className="max-w-sm"
            />
            <Tabs size="sm" selectedKey={groupFilter} onSelectionChange={(k) => setGroupFilter(String(k))} aria-label="Grupo">
              <Tab key="all" title={`Todos (${groupCounts.all})`} />
              {groups.map((g) => <Tab key={g.key} title={`${g.name} (${groupCounts[g.key] || 0})`} />)}
            </Tabs>
            <Select
              aria-label="Protocolo"
              size="sm"
              className="w-40"
              disallowEmptySelection
              selectedKeys={[protocolFilter]}
              onSelectionChange={(keys) => setProtocolFilter(String(Array.from(keys)[0] || 'all'))}
            >
              {[['all', 'Todos los protocolos'], ...protocols.map((p) => [p, PROTOCOL_LABELS[p] || p])].map(([key, label]) => (
                <SelectItem key={key}>{label}</SelectItem>
              ))}
            </Select>
            <Select
              aria-label="Orden"
              size="sm"
              className="w-48"
              disallowEmptySelection
              selectedKeys={[sort]}
              onSelectionChange={(keys) => setSort(String(Array.from(keys)[0] || 'catalog'))}
            >
              {SORT_OPTIONS.map(([key, label]) => <SelectItem key={key}>{label}</SelectItem>)}
            </Select>
            <span className="ml-auto text-xs text-default-400">{filtered.length} juegos</span>
          </div>

          {filtered.length === 0 ? (
            <div className="border-t border-default-200 p-8 text-center text-sm text-default-400">Sin coincidencias.</div>
          ) : (
            sections.map((section) => (
              <div key={section.key} className="border-t border-default-200 pt-4">
                {section.name && (
                  <div className="mb-3 text-sm font-semibold">
                    {section.name}
                    <span className="ml-2 text-xs font-normal text-default-500">{section.games.length} juegos</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {section.games.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      canWrite={canWrite}
                      resetting={resettingId === game.id}
                      onReset={handleReset}
                    />
                  ))}
                </div>
              </div>
            ))
          )}

          {uncatalogued.length > 0 && (
            <div className="border-t border-default-200 pt-4">
              <div className="mb-2 text-sm font-semibold">
                Otras claves en D1
                <span className="ml-2 text-xs font-normal text-default-500">
                  {uncatalogued.length} game_id con estadisticas que no casan con ningun juego del manifest (ids antiguos, mods retirados o el modulo generico de GTA V)
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {uncatalogued.map((item) => (
                  <span key={item.id} className="inline-flex items-center gap-1.5 rounded border border-default-200 px-2 py-1 text-xs text-default-600" title={`ultimo ${formatTs(item.lastUpdated)}`}>
                    <code>{item.id}</code>
                    <span className="text-default-400">{formatNum(item.plays)} plays · {formatNum(item.diamonds)} 💎</span>
                    {canWrite && item.plays + item.diamonds > 0 && (
                      <button
                        type="button"
                        className="text-[11px] text-warning-600 hover:underline disabled:opacity-50"
                        disabled={resettingId === item.id}
                        onClick={() => handleResetRaw(item.id)}
                      >
                        reset stats
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
